import useDeleteAccountWebhook from '@/api/mutations/account/useDeleteAccountWebhook';
import useGetMyAccountWebhooks from '@/api/queries/me/useGetMyAccountWebhooks';
import useGetAvailableWebhookEvents from '@/api/queries/useGetAvailableWebhookEvents';
import { Webhook } from '@/gql/graphql';
import { useModal } from '@/hooks/useModal';
import CreateEditWebhookSidebar, {
  EditWebhookFormData,
} from '@/ui/components/companySettings/components/CreateEditWebhookSidebar';
import {
  AnchorButton,
  Badge,
  Button,
  ManageableItemsList,
  Modal,
  Tooltip,
  useToastContext,
} from '@payaca/ambrosia';

import { FC, useState } from 'react';

type Modals = {
  CREATE_WEBHOOK: undefined;
  UPDATE_WEBHOOK: EditWebhookFormData;
  DELETE_WEBHOOK: { webhookId: string };
};

export type TProps = { readOnly?: boolean };

const CompanyWebhooksTable: FC<TProps> = (props) => {
  const { readOnly = false } = props;

  /**
   * state
   */
  const [pagination, setPagination] = useState<{
    offset: number;
    limit: number;
  }>({
    offset: 0,
    limit: 25,
  });
  const [modalState, modalDispatch] = useModal<keyof Modals, Modals>({
    activeModal: null,
  });
  const { pushError } = useToastContext();

  /**
   * queries
   */
  const { data: accountWebhooks } = useGetMyAccountWebhooks(pagination);
  const { data: allAvailableWebhookEvents } = useGetAvailableWebhookEvents();

  /**
   * mutations
   */
  const { mutateAsync: deleteAccountWebhook } = useDeleteAccountWebhook();

  /**
   * callbacks
   */
  const handleDeleteWebhook = async (webhookId?: string) => {
    try {
      if (!webhookId) {
        throw new Error('Webhook ID is required for deletion');
      }

      await deleteAccountWebhook(webhookId);
    } catch (error) {
      console.error('Error deleting webhook:', error);

      pushError('There was an error deleting the webhook, please try again.');
    }

    modalDispatch({ type: 'CLOSE_MODAL' });
  };

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Webhooks"
          subHeading={
            <p>
              Webhooks allow you to receive real-time notifications about events
              in your account. Check out our{' '}
              <AnchorButton
                openInNewTab
                variant="link"
                href="https://docs.api.payaca.com/#description/webhooks"
              >
                documentation
              </AnchorButton>
            </p>
          }
          buttons={
            readOnly ? undefined : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => modalDispatch({ type: 'CREATE_WEBHOOK' })}
              >
                Create webhook
              </Button>
            )
          }
        />

        <ManageableItemsList.Table
          uniqueKey="id"
          items={accountWebhooks?.items || []}
          itemActions={
            readOnly
              ? undefined
              : [
                  {
                    label: 'Edit',
                    onClick: (row) =>
                      modalDispatch({
                        type: 'UPDATE_WEBHOOK',
                        payload: {
                          webhookId: row.id,
                          name: row.name,
                          url: row.url,
                          events: row.events,
                          apiVersion: row.apiVersion,
                        },
                      }),
                  },
                  {
                    label: 'Delete',
                    onClick: (row) =>
                      modalDispatch({
                        type: 'DELETE_WEBHOOK',
                        payload: { webhookId: row.id },
                      }),
                  },
                ]
          }
          onClickRow={
            readOnly
              ? undefined
              : (row) =>
                  modalDispatch({
                    type: 'UPDATE_WEBHOOK',
                    payload: {
                      webhookId: row.id,
                      name: row.name,
                      url: row.url,
                      events: row.events,
                      apiVersion: row.apiVersion,
                    },
                  })
          }
        >
          <ManageableItemsList.Table.Column header="Name" field="name" />

          <ManageableItemsList.Table.Column header="URL" field="url" />

          <ManageableItemsList.Table.Column
            header="API version"
            field="apiVersion"
          />

          <ManageableItemsList.Table.Column<Webhook, 'events'>
            header="Events"
            field="events"
            render={(events) => (
              <div className="flex flex-wrap gap-2">
                {events.toSorted().map((event) => {
                  const webhook =
                    allAvailableWebhookEvents?.availableWebhookEvents?.find(
                      (we) => we.name === event
                    );
                  const description = webhook?.description || event;

                  return (
                    <Tooltip key={event} tooltipContent={description}>
                      <Badge variant="white">{event}</Badge>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          />
        </ManageableItemsList.Table>

        <ManageableItemsList.PaginationBar
          type="custom"
          pageSize={pagination.limit}
          currentPage={Math.floor(pagination.offset / pagination.limit) + 1}
          totalItems={accountWebhooks?.totalCount || 0}
          onPageChange={(newPage) => {
            setPagination((prev) => ({
              ...prev,
              offset: (newPage - 1) * prev.limit,
            }));
          }}
        />
      </ManageableItemsList>
      <CreateEditWebhookSidebar
        key={modalState.activeModal}
        isOpen={
          modalState.activeModal === 'CREATE_WEBHOOK' ||
          modalState.activeModal === 'UPDATE_WEBHOOK'
        }
        onClose={() =>
          modalDispatch({
            type: 'CLOSE_MODAL',
          })
        }
        defaultValues={
          modalState.activeModal === 'UPDATE_WEBHOOK'
            ? (modalState.payload as Modals['UPDATE_WEBHOOK'])
            : undefined
        }
      />
      <Modal
        isOpen={modalState.activeModal === 'DELETE_WEBHOOK'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        title="Delete webhook"
      >
        <Modal.Body>
          <p>Are you sure you want to delete this webhook?</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={() => handleDeleteWebhook(modalState.payload?.webhookId)}
              colour="negative"
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CompanyWebhooksTable;
